import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, RotateCcw } from 'lucide-react';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import { formatDateTime } from '../utils/dateFormat';

export const RestoreCourseButton = ({ caseId, removed, participantName, onRestored }) => {
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState([]);
  const [confirm, setConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  if (!removed?.length) return null;
  const who = participantName || 'this participant';
  const names = removed.filter((r) => picked.includes(r.id)).map((r) => r.name);
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const restore = async () => {
    setSaving(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/cases/${caseId}/wix-program/restore-courses`, { removal_ids: picked }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('myhelp_token')}` },
      });
      toast.success(`Restored to ${names.join(', ')}`);
      setConfirm(false); setOpen(false); setPicked([]);
      onRestored?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not restore the course');
    } finally { setSaving(false); }
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => { setPicked([]); setOpen(true); }} data-testid="restore-course-btn">
        <RotateCcw className="h-4 w-4 mr-2" />Restore Removed Course
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="restore-course-dialog">
          <DialogHeader>
            <DialogTitle>Restore Removed Course</DialogTitle>
            <DialogDescription>Choose the course(s) to put {who} back into. Their earlier progress comes back with it.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {removed.map((r) => (
              <label key={r.id} className="flex items-center gap-3 rounded-lg border p-3 cursor-pointer" data-testid={`restore-course-option-${r.id}`}>
                <Checkbox checked={picked.includes(r.id)} onCheckedChange={() => toggle(r.id)} />
                <span className="text-sm">
                  <span className="font-medium">{r.name}</span>
                  <span className="block text-xs text-muted-foreground">Removed {formatDateTime(r.removed_at)}{r.removed_by_name ? ` by ${r.removed_by_name}` : ''}</span>
                </span>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="restore-course-cancel-btn">Cancel</Button>
            <Button disabled={!picked.length} onClick={() => setConfirm(true)} data-testid="restore-course-continue-btn">Continue</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AlertDialog open={confirm} onOpenChange={(o) => !saving && setConfirm(o)}>
        <AlertDialogContent data-testid="restore-course-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Restore {who}?</AlertDialogTitle>
            <AlertDialogDescription data-testid="restore-course-confirm-text">
              {participantName || 'This participant'} will be put back into: <b>{names.join(', ')}</b>, with their earlier progress restored.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving} data-testid="restore-course-confirm-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); restore(); }} disabled={saving} data-testid="restore-course-confirm-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Restore
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
